import { rd } from '@passionware/monads';
import { injectConfig } from '@passionware/platform-react';
import { createMatcher } from '@passionware/query-toolkit';
import type { ReactElement } from 'react';
import type {
  EnterpriseListFilter,
  EnterpriseTeam
} from 'v5/api/enterprise/enterprise.api';
import { ItemSelector } from 'v5/patterns/widgets/ItemSelector';
import { EnterpriseTeamIcon } from 'v5/features/core/enterprise/EnterpriseTeamIcon';
import { WithServices } from 'v5/platform/ts/services';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

const EnterpriseTeamPickerInner = injectConfig(ItemSelector<EnterpriseTeam>)
  .fromProps<
    WithServices<[WithEnterpriseService]> & {
      organizationId: string;
      workspaceId: number;
      filter?: EnterpriseListFilter;
    }
  >(api => ({
    useOptions: (query, isDropdownOpen) => {
      const {
        organizationId,
        workspaceId,
        filter = 'active',
        services
      } = api.useProps();
      const teamsQuery = isDropdownOpen
        ? { organizationId, workspaceId }
        : null;
      const teamsRd = services.enterpriseService.useTeams(teamsQuery, filter);
      return rd.map(teamsRd, teams =>
        teams.filter(createMatcher(query, team => team.name)).map(team => ({
          id: team.id.toString(),
          label: team.name,
          content: (
            <div className="flex items-center gap-2">
              <EnterpriseTeamIcon
                name={team.name}
                iconUrl={team.iconUrl}
                iconColor={team.iconColor}
              />
              <span>{team.name}</span>
            </div>
          ),
          extra: team
        }))
      );
    }
  }))
  .transformProps(x => x.passAll);

export type EnterpriseTeamPickerProps = WithServices<
  [WithEnterpriseService]
> & {
  organizationId: string;
  workspaceId: number;
  filter?: EnterpriseListFilter;
  onTeamSelect: (team: EnterpriseTeam) => void;
  children: ReactElement;
  popoverClassName?: string;
};

export function EnterpriseTeamPicker({
  organizationId,
  workspaceId,
  filter,
  onTeamSelect,
  children,
  popoverClassName,
  services
}: EnterpriseTeamPickerProps) {
  return (
    <EnterpriseTeamPickerInner
      services={services}
      organizationId={organizationId}
      workspaceId={workspaceId}
      filter={filter}
      popoverClassName={popoverClassName}
      onSelect={(id, _wasChecked, team) => onTeamSelect(team)}
    >
      {children}
    </EnterpriseTeamPickerInner>
  );
}
